/**
 * The numeric answer control (spec §4.4): a free-text field, the part's fixed
 * unit next to it, and a live echo of how the server will read the input
 * ("tolkas som 6,63 × 10⁻³⁴"), produced by the very same parser the server uses.
 */
import { useId, useLayoutEffect, useRef, useState } from "react";
import { classifyTypedUnit, parseNumericAnswer } from "@shared/grading";
import { useLang, useT } from "@client/i18n";
import { formatScientific } from "@client/lib/format";
import { formatUnit } from "@client/lib/units";
import { Input } from "./ui";

export interface NumericInputProps {
  value: string;
  onChange: (value: string) => void;
  /** Fixed unit label from the content item ("" for dimensionless). */
  unit?: string;
  label?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  id?: string;
  /** Enter (or Ctrl/Cmd+Enter) submits the surrounding form. */
  onSubmit?: () => void;
  /** Extra ids for `aria-describedby`, e.g. the page's keyboard hint (P1-8). */
  describedBy?: string | undefined;
}

/** A touch screen as the main pointer: the phone keyboard needs the helper row (P0-5). */
function hasCoarsePointer(): boolean {
  try {
    return window.matchMedia("(pointer: coarse)").matches;
  } catch {
    return false;
  }
}

/** What each helper key types. The parser reads all of them (`shared/grading.ts`). */
const HELPER_KEYS = [
  { text: "\u2212", label: "−", name: "numeric.key.minus" },
  { text: "×10^", label: "×10^", name: "numeric.key.times" },
  { text: "e", label: "e", name: "numeric.key.e" },
  { text: ",", label: ",", name: "numeric.key.comma" },
] as const;

export function NumericInput({
  value,
  onChange,
  unit = "",
  label,
  disabled,
  autoFocus,
  id,
  onSubmit,
  describedBy,
}: NumericInputProps) {
  const generated = useId();
  const inputId = id ?? generated;
  const t = useT();
  const { lang } = useLang();
  const inputRef = useRef<HTMLInputElement>(null);
  const [coarse] = useState(hasCoarsePointer);
  // The helper row and the decimal pad are for touch; "Textknappsats" gives the full keyboard back.
  const [textKeyboard, setTextKeyboard] = useState(false);
  const numberPad = coarse && !textKeyboard;

  // The caret goes after the inserted text once React has written the new value.
  const pendingCaret = useRef<number | null>(null);
  useLayoutEffect(() => {
    const caret = pendingCaret.current;
    if (caret === null) return;
    pendingCaret.current = null;
    inputRef.current?.focus();
    inputRef.current?.setSelectionRange(caret, caret);
  });

  /** Types `text` at the caret (or over the selection) and puts the caret after it. */
  function insertAtCaret(text: string) {
    const input = inputRef.current;
    const start = input?.selectionStart ?? value.length;
    const end = input?.selectionEnd ?? value.length;
    onChange(value.slice(0, start) + text + value.slice(end));
    pendingCaret.current = start + text.length;
  }

  // Dimensionless parts (unit "") get no suffix, so their id must not be wired
  // into the input's aria-describedby — otherwise it would dangle.
  const unitId = unit ? `${inputId}-unit` : undefined;
  const echoId = `${inputId}-echo`;
  const describedByIds = [unitId, echoId, describedBy].filter(Boolean).join(" ") || undefined;

  const trimmed = value.trim();
  const parsed = trimmed === "" ? null : parseNumericAnswer(trimmed);
  // A unit the student typed must echo back as typed, never silently swapped
  // for the field's fixed unit — same rule `scoreNumeric` grades by
  // (`classifyTypedUnit`), so the echo never promises something grading
  // would not actually do.
  const typedUnit = parsed && "value" in parsed ? parsed.unit : undefined;
  const unitMismatch = typedUnit !== undefined && classifyTypedUnit(typedUnit, unit) === "mismatch";
  const echoUnit = typedUnit ?? unit;
  const echo =
    parsed === null
      ? null
      : "value" in parsed
        ? `${t("numeric.interpreted")} ${formatScientific(parsed.value, lang)}${echoUnit ? ` ${formatUnit(echoUnit)}` : ""}${unitMismatch ? ` — ${t("numeric.unitMismatch")}` : ""}`
        : t("numeric.unparseable");
  const invalid = (parsed !== null && "error" in parsed) || unitMismatch;

  return (
    <div>
      <Input
        id={inputId}
        label={label}
        value={value}
        disabled={disabled}
        autoFocus={autoFocus}
        ref={inputRef}
        inputMode={numberPad ? "decimal" : "text"}
        autoComplete="off"
        spellCheck={false}
        aria-describedby={describedByIds}
        suffix={
          unit ? (
            <span id={unitId}>
              {/* Visible text stays just the unit; the hidden prefix gives
                  screen readers context when this suffix is announced via
                  the input's aria-describedby. */}
              <span className="sr-only">{t("numeric.unit")}: </span>
              {formatUnit(unit)}
            </span>
          ) : undefined
        }
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (onSubmit && event.key === "Enter") {
            event.preventDefault();
            onSubmit();
          }
        }}
      />
      {coarse && (
        <div className="mt-1 flex flex-wrap items-center gap-2">
          {numberPad &&
            HELPER_KEYS.map((key) => (
              <button
                key={key.text}
                type="button"
                disabled={disabled}
                aria-label={t(key.name)}
                // Keeps focus (and the caret) in the field, so the pad does not close.
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => insertAtCaret(key.text)}
                className="inline-flex h-11 min-w-11 items-center justify-center rounded-lg border border-ink-300 px-3 text-base font-medium text-ink-800 active:bg-ink-100 disabled:opacity-50 dark:border-ink-700 dark:text-ink-100 dark:active:bg-ink-800"
              >
                {key.label}
              </button>
            ))}
          <button
            type="button"
            aria-pressed={textKeyboard}
            disabled={disabled}
            onClick={() => {
              setTextKeyboard((on) => !on);
              requestAnimationFrame(() => inputRef.current?.focus());
            }}
            className="ml-auto inline-flex h-11 items-center rounded-lg px-2 text-xs text-ink-600 underline dark:text-ink-300"
          >
            {t("numeric.textKeyboard")}
          </button>
        </div>
      )}
      <p
        id={echoId}
        aria-live="polite"
        className={
          invalid
            ? "mt-1 min-h-[1.25rem] text-xs text-amber-700 dark:text-amber-300"
            : "mt-1 min-h-[1.25rem] text-xs text-ink-500 dark:text-ink-400"
        }
      >
        {echo}
      </p>
    </div>
  );
}
